// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRemoteCollectionStore from '@/app/store/modules/remoteCollection';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsRemoteCollections from '../../api/actionsRemoteCollections';
// Components
import DataTable from '../../../../../common-utils/components/renders/dataTable/DataTable';
// Common
import remoteCollectionFields from '../../fields/remoteCollectionFields';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import Permissions from '@/app/permissions/Permissions';
import REMOTE_COLLECTION_RECONCILIATION_CODE from '../../enums/RemoteCollectionReconciliationCode';
import { columnsRemoteCollections, columnsRemoteCollectionsId } from './columnDef/columnsRemoteCollections';
import { REMOTE_COLLECTION } from '../../api/apiUrls';

/**
 * @param root0
 * @param root0.organizationId
 * @param root0.pageActions
 * @param root0.setTotalRevenue
 * @param root0.setIsRemoteCollectionEmpty
 * @param root0.filtersEventName
 * @returns {JSX} RemoteCollectionsTable
 */
function RemoteCollectionsTable({
    organizationId = undefined,
    pageActions = undefined,
    setTotalRevenue = undefined,
    setIsRemoteCollectionEmpty = undefined,
    filtersEventName
}) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { refetchRemoteCollections: refetchFromStore,
        storeRefetchRemoteCollections, query: queryStored, storeQuery } = useRemoteCollectionStore();

    // Constants
    const QUERY_PRESETS = {
        filters: [
            organizationId ? {
                key: 'provider',
                value: organizationId,
                keepOnReset: true,
                hidden: true
            } : null,
            {
                key: generateNestedFieldAccessor(remoteCollectionFields.RECONCILIATION_CODE),
                value: REMOTE_COLLECTION_RECONCILIATION_CODE.NOT_EMPTY.name,
                keepOnReset: true
            }
        ].filter(Boolean)
    };
    const initialQuery = getInitialQuery(QUERY_PRESETS);

    // State
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, refetch } = actionsRemoteCollections.getRemoteCollections(query, false);
    const { data: totalRevenueResponseData, refetch: refreshTotalRevenue } = actionsRemoteCollections.getTotalRevenue(query, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchRemoteCollections(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    useEffect(() => {
        if (totalRevenueResponseData && setIsRemoteCollectionEmpty && setTotalRevenue) {
            setIsRemoteCollectionEmpty(false);
            setTotalRevenue(totalRevenueResponseData);
        }
    }, [totalRevenueResponseData, setIsRemoteCollectionEmpty, setTotalRevenue]);

    useEffect(() => {
        if (data && data.items && setIsRemoteCollectionEmpty && setTotalRevenue) {
            if (data.items.length === 0) {
                setIsRemoteCollectionEmpty(true);
                setTotalRevenue({
                    [remoteCollectionFields.TOTAL_REVENUE.key]: 0,
                    [remoteCollectionFields.CURRENCY.key]: 'EUR'
                });
            } else {
                refreshTotalRevenue();
            }
        }
    }, [data]);

    const allColumns = columnsRemoteCollections({ query, setQuery, queryStored, storeQuery, setLaunchRefetch, filtersEventName });

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            columnsId={columnsRemoteCollectionsId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQuery}
            setLaunchRefetch={setLaunchRefetch}
            hasPartialResults={true}

            pageActions={pageActions}
            exportProps={!organizationId ?
                {
                    permissions: [Permissions.EXPORT_REMOTE_COLLECTIONS.name],
                    api: REMOTE_COLLECTION.EXPORT
                } : null
            }
        />
    );
}

RemoteCollectionsTable.propTypes = {
    organizationId: PropTypes.string,
    pageActions: PropTypes.node,
    setTotalRevenue: PropTypes.func,
    setIsRemoteCollectionEmpty: PropTypes.func,
    filtersEventName: PropTypes.string
};

export default RemoteCollectionsTable;